/* <info-tabs> — framed product info tabs.
   Content (tab buttons + rich-text panels) is rendered server-side by
   snippets/info-tabs.liquid; this element only handles the marker, the
   scroll arrows/edge-fades, tab switching and keyboard nav. */
class InfoTabs extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;
    this.reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;

    this.row = this.querySelector('.info-tabs__track-row');
    this.track = this.querySelector('.info-tabs__track');
    this.prev = this.querySelector('.info-tabs__prev');
    this.next = this.querySelector('.info-tabs__next');
    this.marker = this.querySelector('.info-tabs__marker');
    this.tabs = Array.prototype.slice.call(this.querySelectorAll('.info-tabs__tab'));
    this.panels = Array.prototype.slice.call(this.querySelectorAll('.info-tabs__panel'));
    if (!this.tabs.length) return;

    var sel = this.tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
    this.index = sel < 0 ? 0 : sel;

    var self = this;
    this.tabs.forEach(function (t, i) { t.addEventListener('click', function () { self.select(i, false); }); });
    if (this.prev) this.prev.addEventListener('click', function () { self.scrollByAmount(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.scrollByAmount(1); });

    var ticking = false;
    this.track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { self.updateScroll(); ticking = false; });
    }, { passive: true });

    this.track.addEventListener('keydown', function (e) {
      var i = self.index, last = self.tabs.length - 1;
      if (e.key === 'ArrowRight') i = Math.min(last, i + 1);
      else if (e.key === 'ArrowLeft') i = Math.max(0, i - 1);
      else if (e.key === 'Home') i = 0;
      else if (e.key === 'End') i = last;
      else return;
      e.preventDefault();
      self.select(i, true);
    });

    this._onResize = function () { self.positionMarker(); self.updateScroll(); };
    addEventListener('resize', this._onResize);

    this.select(this.index, false, true);
    requestAnimationFrame(function () { self.positionMarker(); self.updateScroll(); });
  }

  disconnectedCallback() { if (this._onResize) removeEventListener('resize', this._onResize); }

  scrollByAmount(dir) {
    // always native-smooth: the arrow nudge is a deliberate, gentle motion
    this.track.scrollBy({ left: dir * this.track.clientWidth * 0.7, behavior: 'smooth' });
  }

  select(i, focus, instant) {
    this.index = i;
    this.tabs.forEach(function (t, k) {
      var on = k === i;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
    });
    this.positionMarker();
    this.centerTab(this.tabs[i]);
    this.showPanel(this.panels[i], instant);
    if (focus) this.tabs[i].focus();
  }

  showPanel(next, instant) {
    if (!next) return;
    var current = this.panels.find(function (p) { return !p.hidden && p !== next; });
    var self = this;
    if (current && !instant) {
      current.classList.add('is-swapping');
      clearTimeout(this._swap);
      this._swap = setTimeout(function () {
        current.hidden = true;
        current.classList.remove('is-swapping');
        self.reveal(next, false);
      }, 160);
    } else {
      this.panels.forEach(function (p) { if (p !== next) { p.hidden = true; p.classList.remove('is-swapping'); } });
      this.reveal(next, instant);
    }
  }

  reveal(panel, instant) {
    panel.hidden = false;
    if (instant) { panel.classList.remove('is-swapping'); return; }
    panel.classList.add('is-swapping');
    requestAnimationFrame(function () { requestAnimationFrame(function () { panel.classList.remove('is-swapping'); }); });
  }

  positionMarker() {
    var t = this.tabs[this.index];
    if (!t || !this.marker) return;
    this.marker.style.width = t.offsetWidth + 'px';
    this.marker.style.transform = 'translateX(' + t.offsetLeft + 'px)';
  }

  centerTab(t) {
    if (!t) return;
    var target = t.offsetLeft - (this.track.clientWidth - t.offsetWidth) / 2;
    this.track.scrollTo({ left: Math.max(0, target), behavior: this.reduce ? 'auto' : 'smooth' });
  }

  updateScroll() {
    var el = this.track, max = el.scrollWidth - el.clientWidth;
    this.row.classList.toggle('can-left', el.scrollLeft > 1);
    this.row.classList.toggle('can-right', el.scrollLeft < max - 1);
  }
}
customElements.define('info-tabs', InfoTabs);
